import axios from 'axios';
import { API_URL } from '../components/config';  // Import API_URL from config

const api = axios.create({
  baseURL: API_URL,  // Use imported API_URL
  headers: {
    'Content-Type': 'application/json',
  }
});

// Update this interceptor to use 'authToken' instead of 'token'
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken'); // Changed from 'token' to 'authToken'
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // If this is a file download request (blob response type), ensure we handle it properly
      if (config.responseType === 'blob') {
        // Store the auth token that will be needed after the blob is received
        config.headers['X-Auth-Token'] = token;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Decode JWT expiry from token payload (no signature check needed client-side)
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

// Silently refresh the token if it expires within 24 hours
let _refreshing = false;
const maybeRefreshToken = async () => {
  if (_refreshing) return;
  const token = localStorage.getItem('authToken');
  if (!token) return;
  const expiry = getTokenExpiry(token);
  if (!expiry) return;
  const msLeft = expiry - Date.now();
  if (msLeft < 24 * 60 * 60 * 1000) { // less than 24h left
    _refreshing = true;
    try {
      const res = await axios.post(
        `${API_URL}/api/users/refresh-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.token) {
        localStorage.setItem('authToken', res.data.token);
      }
    } catch {
      // ignore refresh failures silently
    } finally {
      _refreshing = false;
    }
  }
};

// Add a response interceptor to handle expired tokens
api.interceptors.response.use(
  (response) => {
    // On any successful response, opportunistically refresh if token is almost expired
    maybeRefreshToken();
    return response;
  },
  (error) => {
    // If token expired, redirect to login and preserve the current page
    if (error.response && error.response.status === 401) {
      // Clear authentication data
      localStorage.removeItem('authToken');
      localStorage.removeItem('encryptionKey');
      localStorage.removeItem('user');

      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?expired=true&returnTo=${returnTo}`;
    }
    return Promise.reject(error);
  }
);

// Add a file download helper method
api.downloadFile = async (url, filename) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await axios({
      url: `${API_URL}${url}`,  // Use imported API_URL
      method: 'GET',
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // Create a download link and click it
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    return true;
  } catch (error) {
    console.error('File download error:', error);
    return false;
  }
};

export default api;